import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../../lib/cn";

type ButtonDataRowProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  as?: "button";
  selected?: boolean;
  interactive?: boolean;
  responsive?: "stack" | "inline";
};

type StaticDataRowProps = HTMLAttributes<HTMLDivElement> & {
  as: "div";
  selected?: boolean;
  interactive?: false;
  responsive?: "stack" | "inline";
  disabled?: never;
  type?: never;
  onClick?: never;
};

type DataRowProps = ButtonDataRowProps | StaticDataRowProps;

export const DataRow = forwardRef<HTMLButtonElement | HTMLDivElement, DataRowProps>(
  (props, ref) => {
    if (props.as === "div") {
      const { as: _as, selected, interactive = false, responsive = "stack", className, ...divProps } = props;
      return (
        <div
          ref={ref as Ref<HTMLDivElement>}
          className={cn(
            "ui-data-row",
            `ui-data-row-responsive-${responsive}`,
            interactive && "ui-data-row-interactive",
            selected && "ui-data-row-selected",
            className
          )}
          {...divProps}
        />
      );
    }

    const {
      as: _as,
      selected,
      interactive = true,
      responsive = "stack",
      className,
      type = "button",
      "aria-current": ariaCurrentProp,
      ...buttonProps
    } = props;
    const ariaCurrent = ariaCurrentProp ?? (selected ? "true" : undefined);
    return (
      <button
        ref={ref as Ref<HTMLButtonElement>}
        type={type}
        aria-current={ariaCurrent}
        className={cn(
          "ui-data-row",
          `ui-data-row-responsive-${responsive}`,
          interactive && "ui-data-row-interactive",
          selected && "ui-data-row-selected",
          className
        )}
        {...buttonProps}
      />
    );
  }
);

DataRow.displayName = "DataRow";

export function DataRowMain({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("ui-data-row-main", className)} {...props} />;
}

export function DataRowBadges({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("ui-data-row-badges", className)} {...props} />;
}

export function DataRowMeta({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("ui-data-row-meta", className)} {...props} />;
}

export function DataRowValue({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("ui-data-row-value", className)} {...props} />;
}

export function DataRowLabelValue({ label, value, className, ...props }: HTMLAttributes<HTMLSpanElement> & { label: ReactNode; value: ReactNode }) {
  return (
    <span className={cn("ui-data-row-label-value", className)} {...props}>
      <strong>{label}</strong>
      <small>{value}</small>
    </span>
  );
}

export function DataRowMedia({ className, children, ...props }: HTMLAttributes<HTMLSpanElement> & { children: ReactNode }) {
  return (
    <span className={cn("ui-data-row-media", className)} {...props}>
      {children}
    </span>
  );
}

export function DataRowList({
  className,
  density = "default",
  role = "list",
  ...props
}: HTMLAttributes<HTMLDivElement> & { density?: "default" | "compact" }) {
  return <div className={cn("ui-data-row-list", density === "compact" && "ui-data-row-list-compact", className)} role={role} {...props} />;
}

export function DataRowListItem({ className, role = "listitem", ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("ui-data-row-list-item", className)} role={role} {...props} />;
}

export function DataRowStack({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("ui-data-row-stack", className)} {...props} />;
}
